import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaEnvelope, FaQuestionCircle, FaShieldAlt, FaRocket, FaHeadset } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Header from '../components/Header';

const faqs = [
  {
    question: 'Is this service really free?',
    answer: 'Yes! Our privacy policy generator is completely free with no hidden costs or subscriptions.'
  },
  {
    question: 'Are the generated policies legally binding?',
    answer: 'The policies are based on common legal requirements, but we recommend legal review for your specific situation.'
  },
  {
    question: 'Can I customize the generated policy?',
    answer: 'Yes, you can edit and adapt the generated documents to fit your business needs.'
  },
  {
    question: 'How do I get support?',
    answer: 'Use the contact form to reach our team for any technical or legal questions.'
  },
  {
    question: 'Can I generate other legal documents besides privacy policies?',
    answer: 'Absolutely! You can generate Terms of Service, Cookie Policy, Data Processing Agreement, and more.'
  },
  {
    question: 'Is my data safe and private?',
    answer: 'Yes, we take your privacy seriously. Your information is never shared with third parties and is protected by industry-standard security.'
  },
  {
    question: 'How quickly will I receive my generated document?',
    answer: 'Documents are generated instantly and available for download or sharing as soon as you complete the form.'
  },
  {
    question: 'Can I use these documents for my mobile app or SaaS platform?',
    answer: 'Yes! Our documents are suitable for websites, mobile apps, SaaS platforms, e-commerce, and more.'
  }
];

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact AppActs - Get Support & Help for Your Business';
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success("Thank you for your message! We'll get back to you soon.");
        setFormData({ name: '', email: '', subject: '', message: '', type: 'general' });
      } else {
        const data = await res.json();
        toast.error(data.error || 'An error occurred.');
      }
    } catch (err) {
      toast.error('An error occurred.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact AppActs - Get Support & Help for Your Business</title>
        <meta name="description" content="Contact AppActs for support, questions, or feedback. Get help with legal documents, URL shortening, customer support, and business tools. We're here to help indie developers and vibe coders." />
        <meta name="keywords" content="contact AppActs, support, help, legal documents, URL shortener, customer support, indie developers, vibe coders" />
        <meta property="og:title" content="Contact AppActs - Get Support & Help" />
        <meta property="og:description" content="Contact AppActs for support, questions, or feedback. Get help with legal documents, URL shortening, customer support, and business tools." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact AppActs - Get Support & Help" />
        <meta name="twitter:description" content="Contact AppActs for support, questions, or feedback. Get help with legal documents, URL shortening, customer support, and business tools." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
        <Header />
        {/* Hero Section */}
        <section className="py-32 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-light mb-6 leading-tight">
                Contact Us
              </h1>
              <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
                Have a question, suggestion, or need support? We're here to help indie developers and vibe coders succeed.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* FAQ Section */}
              <div>
                <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
                  Frequently Asked Questions
                </h2>
                <FAQAccordion />
              </div>
              
              {/* Contact Form */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h3 className="text-2xl font-light text-white mb-6">Send us a Message</h3>
                <p className="text-white/70 mb-8">Fill out the form below and our team will get back to you as soon as possible.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30 text-white placeholder-white/50"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30 text-white placeholder-white/50"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Message Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30 text-white"
                    >
                      <option value="general">General Question</option>
                      <option value="support">Technical Support</option>
                      <option value="feature">Feature Request</option>
                      <option value="bug">Bug Report</option>
                      <option value="legal">Legal Question</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30 text-white placeholder-white/50"
                      placeholder="Brief subject of your message"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30 text-white placeholder-white/50"
                      placeholder="Please describe your question or concern in detail..."
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="w-full flex items-center justify-center bg-white text-black font-medium py-3 rounded-lg hover:bg-white/90 transition-colors duration-200"
                  >
                    <FaEnvelope className="mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

// FAQ Accordion Component
function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="space-y-4">
      {faqs.map((faq, idx) => (
        <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          <button
            type="button"
            className="w-full flex justify-between items-center p-6 text-left focus:outline-none text-white hover:text-white/80 transition-colors"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
          >
            <span className="font-medium text-lg">{faq.question}</span>
            <span className={`ml-4 transition-transform duration-200 ${openIndex === idx ? 'rotate-90' : ''}`}>▶</span>
          </button>
          {openIndex === idx && (
            <div className="px-6 pb-6 text-white/70 text-base leading-relaxed">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Contact; 